<script lang="ts">
  import { _, locale } from 'svelte-i18n';
  import { createEventDispatcher } from 'svelte';
  import { Link } from 'svelte-routing';
  import type { ReportEntryDto, ReportFilterField, DataSourceInfo } from '../../api';
  import { userSettings } from '../../stores/settingsStore';
  import { formatDate } from '../../settings';
  import {
    FileText,
    ChevronDown,
    ChevronRight,
    AlertTriangle,
    Info,
    ExternalLink,
  } from 'lucide-svelte';
  import {
    Spinner,
    TableSortHeader,
    FilterToolbar,
    PaginationBar,
    Badge,
    AiDisclaimer,
  } from '../ui/index';
  import type { FilterDef, FilterValue } from '../ui/FilterToolbar.svelte';
  import {
    shouldShowSubtotal,
    getSubtotalAmount,
    formatSubtotalAmount,
  } from '../../handwerkerSubtotal';

  export let entries: ReportEntryDto[] = [];
  export let loading = false;
  export let sortBy: string | null = 'entry_date';
  export let sortOrder: 'asc' | 'desc' | null = 'desc';
  export let filterState: Record<string, string | number | null> = {};
  export let page = 0;
  export let pageSize = 20;
  export let totalCount: number | null = null;
  export let hasMore = false;
  export let filterSchema: ReportFilterField[] = [];
  export let dataSources: DataSourceInfo[] = [];

  const dispatch = createEventDispatcher<{
    sort: { key: string; order: 'asc' | 'desc' | null };
    filter: { key: string; value: FilterValue };
    clearAll: void;
    pageChange: number;
    pageSizeChange: number;
  }>();

  let expandedRows = new Set<string>();

  function toggleRow(id: string) {
    if (expandedRows.has(id)) {
      expandedRows.delete(id);
    } else {
      expandedRows.add(id);
    }
    expandedRows = expandedRows;
  }

  function formatAmount(amount: unknown): string {
    const num = Number(amount);
    if (isNaN(num)) return '-';
    return num.toLocaleString($userSettings.locale, { style: 'currency', currency: 'EUR' });
  }

  function handleSort(e: CustomEvent<{ key: string; order: 'asc' | 'desc' | null }>) {
    dispatch('sort', e.detail);
  }

  const yearOptions = (() => {
    const currentYear = new Date().getFullYear();
    const years: string[] = [];
    for (let y = currentYear; y >= currentYear - 10; y--) {
      years.push(y.toString());
    }
    return years;
  })();

  $: availableFilters = buildFiltersFromSchema(filterSchema, yearOptions);

  function buildFiltersFromSchema(schema: ReportFilterField[], years: string[]): FilterDef[] {
    const filters: FilterDef[] = [];
    for (const field of schema) {
      if (field.name === 'year') {
        filters.push({
          key: 'year',
          label: field.displayName,
          type: 'enum',
          options: years,
          optionLabels: years,
        });
      } else if (field.name === 'deductible' && field.options) {
        filters.push({
          key: 'deductible',
          label: field.displayName,
          type: 'enum',
          options: field.options,
          optionLabels: field.options.map((o) => {
            if (o === 'yes') return $_('reports.handwerker.deductible');
            if (o === 'no') return $_('reports.handwerker.non_deductible');
            if (o === 'unclear') return $_('reports.handwerker.unclear');
            return o;
          }),
        });
      } else if (field.fieldType === 'Select' && field.options) {
        filters.push({
          key: field.name,
          label: field.displayName,
          type: 'enum',
          options: field.options,
          optionLabels: field.options,
        });
      }
    }
    return filters;
  }

  $: toolbarFilters = buildToolbarFilters(filterState);

  function buildToolbarFilters(
    state: Record<string, string | number | null>,
  ): Record<string, FilterValue> {
    const result: Record<string, FilterValue> = {};
    if (state.year) result.year = [String(state.year)];
    if (state.deductible) result.deductible = [String(state.deductible)];
    return result;
  }

  function handleFilterChange(e: CustomEvent<{ key: string; value: FilterValue }>) {
    dispatch('filter', e.detail);
  }

  function handleFilterRemove(e: CustomEvent<{ key: string }>) {
    dispatch('filter', { key: e.detail.key, value: null });
  }

  function getLineItems(data: Record<string, unknown> | undefined): Array<Record<string, unknown>> {
    return Array.isArray(data?.lineItems)
      ? (data!.lineItems as Array<Record<string, unknown>>)
      : [];
  }

  function getEntryDate(entry: ReportEntryDto): string {
    return (entry.data?.invoiceDate as string | undefined) || entry.entryDate;
  }

  function handleClearAll() {
    dispatch('clearAll');
  }

  function getDeductibleVariant(val: unknown): 'success' | 'error' | 'warning' {
    if (val === true) return 'success';
    if (val === false) return 'error';
    return 'warning';
  }

  function getDeductibleLabel(val: unknown): string {
    if (val === true) return $_('reports.handwerker.deductible');
    if (val === false) return $_('reports.handwerker.non_deductible');
    return $_('reports.handwerker.unclear');
  }

  $: totalDeductible = entries.reduce(
    (sum, e) => sum + (Number(e.data?.totalDeductibleBrutto) || 0),
    0,
  );
</script>

<div class="bg-surface border border-border rounded-lg overflow-hidden">
  <FilterToolbar
    {availableFilters}
    activeFilters={toolbarFilters}
    on:filterChange={handleFilterChange}
    on:filterRemove={handleFilterRemove}
    on:clearAll={handleClearAll}
  />

  {#if loading}
    <div class="flex justify-center py-12">
      <Spinner size="lg" label={$_('messages.loading')} />
    </div>
  {:else if entries.length === 0}
    <div class="flex flex-col items-center justify-center py-12 px-4 text-center">
      <FileText class="w-8 h-8 text-text-muted mb-2" strokeWidth={1.5} />
      <p class="text-sm text-text-secondary">
        {$_('reports.handwerker.empty_state', { values: { year: filterState.year || '' } })}
      </p>
    </div>
  {:else}
    <div class="overflow-x-auto">
      <table class="w-full">
        <thead>
          <tr class="border-b border-border">
            <th class="w-8 px-2"></th>
            <TableSortHeader
              label={$_('reports.handwerker.col_date')}
              sortKey="entry_date"
              currentSortBy={sortBy}
              currentSortOrder={sortOrder}
              on:sort={handleSort}
            />
            <th
              class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-muted"
            >
              {$_('reports.handwerker.col_vendor')}
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-muted hidden sm:table-cell"
            >
              {$_('reports.handwerker.col_positions')}
            </th>
            <TableSortHeader
              label={$_('reports.handwerker.col_deductible_total')}
              sortKey="amount"
              currentSortBy={sortBy}
              currentSortOrder={sortOrder}
              on:sort={handleSort}
            />
            <th
              class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-muted hidden sm:table-cell"
            >
              {$_('reports.table.document')}
            </th>
          </tr>
        </thead>
        <tbody>
          {#each entries as entry (entry.id)}
            {@const lineItems = getLineItems(entry.data)}
            {@const isExpanded = expandedRows.has(entry.id)}
            {@const hasFailed = entry.data?.extractionFailed === true}

            <tr
              class="border-b border-border/50 transition-colors hover:bg-surface-hover cursor-pointer"
              on:click={() => toggleRow(entry.id)}
            >
              <td class="px-2 py-3 text-text-muted">
                {#if lineItems.length > 0}
                  {#if isExpanded}
                    <ChevronDown class="w-4 h-4" />
                  {:else}
                    <ChevronRight class="w-4 h-4" />
                  {/if}
                {/if}
              </td>
              <td class="px-4 py-3 text-sm text-text-muted whitespace-nowrap">
                {formatDate(getEntryDate(entry), $userSettings)}
              </td>
              <td class="px-4 py-3 text-sm text-text">
                <div class="flex items-center gap-2">
                  {entry.data?.vendor || '-'}
                  {#if hasFailed}
                    <span
                      class="inline-flex items-center gap-1 text-warning"
                      title={$_('reports.handwerker.extraction_failed')}
                    >
                      <AlertTriangle class="w-3.5 h-3.5" />
                    </span>
                  {/if}
                </div>
              </td>
              <td class="px-4 py-3 text-sm text-text-secondary hidden sm:table-cell">
                {lineItems.length}
              </td>
              <td class="px-4 py-3 text-sm font-mono text-right whitespace-nowrap text-text">
                {formatAmount(entry.data?.totalDeductibleBrutto)}
              </td>
              <td class="px-4 py-3 hidden sm:table-cell">
                <Link
                  to="/documents/{entry.documentId}"
                  class="text-sm text-accent hover:text-accent-hover hover:underline"
                >
                  {entry.data?.displayName || entry.data?.fileName || entry.documentId}
                </Link>
              </td>
            </tr>

            {#if isExpanded && lineItems.length > 0}
              <tr>
                <td colspan="6" class="px-0 py-0">
                  <div class="bg-surface-hover/50 px-8 py-3">
                    {#if hasFailed}
                      <div
                        class="mb-3 px-3 py-2 bg-warning/10 border border-warning/30 rounded text-sm text-warning flex items-center gap-2"
                      >
                        <AlertTriangle class="w-4 h-4 flex-shrink-0" />
                        {$_('reports.handwerker.extraction_failed')}
                      </div>
                    {/if}
                    <table class="w-full text-xs">
                      <thead>
                        <tr class="border-b border-border/50">
                          <th class="px-2 py-1.5 text-left font-semibold text-text-muted"
                            >{$_('reports.handwerker.col_description')}</th
                          >
                          <th class="px-2 py-1.5 text-right font-semibold text-text-muted"
                            >{$_('reports.handwerker.col_netto')}</th
                          >
                          <th
                            class="px-2 py-1.5 text-right font-semibold text-text-muted hidden sm:table-cell"
                            >{$_('reports.handwerker.col_vat')}</th
                          >
                          <th class="px-2 py-1.5 text-right font-semibold text-text-muted"
                            >{$_('reports.handwerker.col_brutto')}</th
                          >
                          <th class="px-2 py-1.5 text-left font-semibold text-text-muted"
                            >{$_('reports.handwerker.col_status')}</th
                          >
                          <th
                            class="px-2 py-1.5 text-left font-semibold text-text-muted hidden md:table-cell"
                            >{$_('reports.handwerker.col_reason')}</th
                          >
                        </tr>
                      </thead>
                      <tbody>
                        {#each lineItems as item, i (i)}
                          <tr class="border-b border-border/30">
                            <td class="px-2 py-1.5 text-text">{item.description || '-'}</td>
                            <td class="px-2 py-1.5 text-right font-mono text-text-muted"
                              >{formatAmount(item.amountNetto)}</td
                            >
                            <td
                              class="px-2 py-1.5 text-right font-mono text-text-muted hidden sm:table-cell"
                              >{item.vatRate != null ? `${item.vatRate}%` : '-'}</td
                            >
                            <td class="px-2 py-1.5 text-right font-mono text-text"
                              >{formatAmount(item.amountBrutto)}</td
                            >
                            <td class="px-2 py-1.5">
                              <Badge variant={getDeductibleVariant(item.deductible)}>
                                {getDeductibleLabel(item.deductible)}
                              </Badge>
                            </td>
                            <td class="px-2 py-1.5 text-text-muted hidden md:table-cell"
                              >{item.reason || '-'}</td
                            >
                          </tr>
                        {/each}
                      </tbody>
                      {#if shouldShowSubtotal(entry.data)}
                        <tfoot>
                          <tr class="border-t border-border/50">
                            <td
                              colspan="3"
                              class="px-2 py-1.5 text-xs font-semibold text-text-secondary text-right"
                            >
                              {$_('reports.handwerker.subtotal_deductible')}
                            </td>
                            <td class="px-2 py-1.5 text-right font-mono font-semibold text-text">
                              {formatSubtotalAmount(
                                getSubtotalAmount(entry.data),
                                $userSettings.locale,
                              )}
                            </td>
                            <td colspan="2" class="hidden md:table-cell"></td>
                          </tr>
                        </tfoot>
                      {/if}
                    </table>
                  </div>
                </td>
              </tr>
            {/if}
          {/each}
        </tbody>
        <tfoot>
          <tr class="border-t-2 border-border bg-surface-hover/30">
            <td colspan="4" class="px-4 py-3 text-sm font-semibold text-text text-right">
              {$_('reports.handwerker.total_deductible')}
            </td>
            <td class="px-4 py-3 text-sm font-mono font-semibold text-right text-accent">
              {formatAmount(totalDeductible)}
            </td>
            <td class="hidden sm:table-cell"></td>
          </tr>
        </tfoot>
      </table>
    </div>

    <PaginationBar
      {page}
      {pageSize}
      {totalCount}
      {hasMore}
      hasPrev={page > 0}
      on:pageChange={(e) => dispatch('pageChange', e.detail)}
      on:pageSizeChange={(e) => dispatch('pageSizeChange', e.detail)}
    />

    <AiDisclaimer>
      <div class="flex items-start gap-2">
        <Info class="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-info" />
        <div>
          <p class="font-semibold text-text-secondary mb-1">
            {$_('reports.handwerker.disclaimer_title')}
          </p>
          <ul class="list-disc list-inside space-y-0.5">
            <li>{$_('reports.handwerker.disclaimer_residence')}</li>
            <li>{$_('reports.handwerker.disclaimer_deductible')}</li>
          </ul>
          <a
            href={$_('reports.handwerker.disclaimer_url')}
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-1 mt-1.5 text-accent hover:text-accent-hover hover:underline"
          >
            {$_('reports.handwerker.disclaimer_link')}
            <ExternalLink class="w-3 h-3" />
          </a>
        </div>
      </div>

      {#if dataSources.length > 0}
        <div class="flex items-start gap-2">
          <Info class="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-info" />
          <div>
            <p class="font-semibold text-text-secondary mb-1">{$_('reports.data_sources_title')}</p>
            <p class="mb-1">{$_('reports.data_sources_description')}</p>
            <ul class="list-disc list-inside space-y-0.5">
              {#each dataSources as source (source.label)}
                <li>{source.label}</li>
              {/each}
            </ul>
          </div>
        </div>
      {/if}
    </AiDisclaimer>
  {/if}
</div>
